import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SideMenu from '../SideMenu/SideMenu';
import Product from '../../Types/Product';
import { translate } from '../../Services/translation';
import BuySampleButton from '../BuyButton/BuySampleButton';
import Icon from '../Icon';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';
import ProductItemImage from '../../Types/ProductItemImage';
import clsx from 'clsx';
import FavoriteButton from '../FavoriteButton/FavoriteButton';
import {
  WishListActions,
  addProductToWishList,
  removeProductFromWishList,
} from '../../Reducers/WishList.reducer';
import { useTypedDispatch } from '../../Hooks/useTypedDispatch';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { StockStatusLevel } from '../../constants';

const getMediaColClass = (idx) => {
  switch (idx) {
    default:
      return '';
    case 2:
    case 3:
    case 6:
    case 7:
      return 'col-span-2';
  }
};

const ProductQuickViewModal = ({
  product,
  open,
  disableBuyOption,
  onClosed,
}: {
  product: Product;
  onClosed: () => void;
  open: boolean;
  disableBuyOption: boolean;
}) => {
  const dispatch = useTypedDispatch();
  const { current: currentUser } = useTypedSelector(
    (state) => state.userInfo
  );
  const { favorite } = product;
  const [isFavorite, setIsFavorite] = useState<boolean>(favorite);
  const [mediaList, setMediaList] = useState<
    (ProductItemImage | string)[]
  >([]);

  const onAddToFavoriteClick = useCallback(() => {
    if (isFavorite) {
      dispatch(removeProductFromWishList(product));
      dispatch(WishListActions.removeWishListItem({ product }));
    } else {
      dispatch(addProductToWishList(product));
    }
    setIsFavorite(!isFavorite);
  }, [dispatch, isFavorite, product]);

  useEffect(() => {
    const list = [...(product.images ?? [])] as (
      | ProductItemImage
      | string
    )[];

    if (product.videoUrl) {
      list.splice(2, 0, product.videoUrl);
    }

    setMediaList(list.slice(0, 8));
  }, [product.images, product.videoUrl]);

  return (
    <SideMenu
      id={`product-quick-view-${product.id}`}
      from="bottom"
      open={open}
      onClosed={onClosed}
      title={product.name}
      className="!max-h-[80%] !rounded-b-none"
      contentClassName="overflow-auto px-4"
      useSideMenu={true}
    >
      <div className="grid grid-cols-1 gap-5 overflow-auto">
        <div className="flex flex-col gap-2">
          {product.stockStatus?.level !== StockStatusLevel.Inactive &&
            !product.disableBuyOption &&
            !disableBuyOption &&
            product.samples?.map((sample) => (
              <BuySampleButton
                {...sample}
                className="btn--large w-full disabled:!opacity-100"
                styleClassName="btn--outline-black"
                key={`sample-${sample.articleNumber}`}
              />
            ))}
          {currentUser && (
            <button
              className="btn btn--large btn--outline-black w-full"
              onClick={onAddToFavoriteClick}
            >
              <span className="flex items-center gap-3">
                <Icon
                  name={isFavorite ? 'heart-filled' : 'heart'}
                  className={clsx(
                    'h-6 w-6',
                    isFavorite && 'text-success-300'
                  )}
                />
                {!isFavorite &&
                  translate('product-quick-view.add-to-favourites')}
                {isFavorite &&
                  translate('product-quick-view.remove-from-favourites')}
              </span>
            </button>
          )}
          {product.url && (
            <a
              href={product.url}
              className="btn btn--black btn--large w-full"
            >
              {translate('product-quick-view.go-to-product')}
              <Icon name="chevron-right" className="ml-3 h-6 w-6" />
            </a>
          )}
        </div>
        <div>
          <h4 className="mb-2">
            {translate('product-quick-view.photos-and-images')}
          </h4>
          <div className="mb-4 grid grid-cols-2 gap-4">
            {mediaList.map((media, idx) =>
              typeof media === 'string' ? (
                <div
                  className={clsx(
                    'w-full overflow-hidden rounded bg-gray-100 object-cover aspect-video',
                    getMediaColClass(idx)
                  )}
                  key={media + idx}
                >
                  <video
                    src={media}
                    muted
                    autoPlay
                    loop
                    playsInline
                    className="scale-110 rounded object-cover aspect-video"
                  />
                </div>
              ) : (
                <ResponsiveImage
                  {...media.imageModel}
                  key={media.originalSrc + idx}
                  imgProps={{
                    className: clsx(
                      'rounded object-cover w-full h-full',
                      idx == 7 ? 'aspect-[4/3]' : 'aspect-[3/4]'
                    ),
                  }}
                  pictureProps={{ className: getMediaColClass(idx) }}
                />
              )
            )}
          </div>
        </div>
      </div>
    </SideMenu>
  );
};

export default ProductQuickViewModal;
